// Invitations (/settings/invitations) — List archetype (invitations-settings
// spec): PageHeader → invitations table; create lives in a Drawer deep-linked
// via `?new=1`. Container owns queries/mutations and the URL state.

import { useEffect } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { components } from "../api/schema";
import { apiClient } from "../lib/apiClient";
import { useCallerAuthority } from "../lib/authority";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { MAX_PAGE_SIZE } from "../lib/pagination";
import { queryKeys } from "../lib/queryKeys";
import { useListQuery } from "../lib/useListQuery";
import {
  parseInvitationsSearch,
  type InvitationsSearch,
} from "../lib/routerSearch";
import {
  mergeSearch,
  useTypedNavigate,
  useTypedSearch,
} from "../lib/typedRouter";
import { Button, Page, PageHeader, useToast } from "../ui";
import { InvitationsTable } from "./invitations/InvitationsTable";
import { InviteDrawer } from "./invitations/InviteDrawer";
import { uniqueIds, userMap, usersListQuery } from "./shared/userDirectory";

type Invitation = components["schemas"]["InvitationResponse"];
type InvitationCreate = components["schemas"]["InvitationCreate"];
type MemberRole = components["schemas"]["MemberRole"];
type Team = components["schemas"]["TeamResponse"];
type User = components["schemas"]["UserResponse"];

// `GET /v1/users?ids=` is capped at 100 ids (docs/04 §11 `users` bullet).
const USER_IDS_CAP = 100;

export function InvitationsSettings() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useTypedNavigate<InvitationsSearch>();
  // Only the route-owned `new` param is read; `?agent` is ignored here.
  const search = useTypedSearch(parseInvitationsSearch);

  // Affordance gate + role ceiling from membership-derived authority.
  const authority = useCallerAuthority();

  const isInviteRequested = search.new === 1;
  // While authority is pending/errored the drawer renders nothing and `?new`
  // is retained — stripping before it RESOLVES would kill admin deep-links.
  const isInviteDrawerOpen = isInviteRequested && authority.canManage;
  const authorityResolved = !authority.isPending && !authority.isError;

  useEffect(() => {
    if (isInviteRequested && authorityResolved && !authority.canManage) {
      void navigate({
        replace: true,
        search: mergeSearch<InvitationsSearch>({ new: undefined }),
      });
    }
  }, [isInviteRequested, authorityResolved, authority.canManage, navigate]);

  function openInviteDrawer(): void {
    void navigate({
      search: mergeSearch<InvitationsSearch>({ new: 1 }),
    });
  }

  function closeInviteDrawer(): void {
    // Drawer fields reset by unmount; the stale create error resets here.
    createInvitation.reset();
    void navigate({
      replace: true,
      search: mergeSearch<InvitationsSearch>({ new: undefined }),
    });
  }

  // Teams page to exhaustion: drawer select + row name resolver stay complete.
  const teams = useQuery({
    queryKey: queryKeys.teams.list({ surface: "invitations-settings" }),
    queryFn: async () => {
      const collected: Team[] = [];
      let pageToken: string | undefined;
      do {
        const { data, error } = await apiClient.GET("/v1/teams", {
          params: {
            query: { page_size: MAX_PAGE_SIZE, page_token: pageToken },
          },
        });
        if (error) {
          throw new ApiRequestError(error);
        }
        collected.push(...data.items);
        pageToken = data.next_page_token ?? undefined;
      } while (pageToken);
      return collected;
    },
  });

  const invitations = useListQuery("/v1/invitations", {
    queryKey: queryKeys.invitations.list(),
  });

  const createInvitation = useMutation({
    mutationFn: async (body: InvitationCreate) => {
      const { error } = await apiClient.POST("/v1/invitations", { body });
      if (error) {
        throw new ApiRequestError(error);
      }
    },
    onSuccess: async () => {
      toast.success("Invitation sent");
      closeInviteDrawer();
      await queryClient.invalidateQueries({
        queryKey: queryKeys.invitations.all,
      });
    },
    onError: (error) => {
      toast.error("Invitation failed", {
        description: requestErrorDescription(
          error,
          "Invitation request failed.",
        ),
      });
    },
  });

  const revokeInvitation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await apiClient.DELETE("/v1/invitations/{id}", {
        params: { path: { id } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
    },
    onSuccess: async () => {
      toast.success("Invitation revoked");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.invitations.all,
      });
    },
    onError: (error) => {
      toast.error("Revoke failed", {
        description: requestErrorDescription(
          error,
          "Invitation request failed.",
        ),
      });
    },
  });

  // Resolve inviter/accepter names via the human read, cap 100 ids.
  const inviterIds = uniqueIds(
    invitations.items.flatMap((invitation) => [
      invitation.created_by,
      invitation.accepted_by,
    ]),
  ).slice(0, USER_IDS_CAP);
  const inviterIdsParam = inviterIds.join(",");

  const users = useQuery({
    enabled: inviterIdsParam.length > 0,
    queryKey: queryKeys.users.list({
      ids: inviterIdsParam,
      surface: "invitations-settings",
    }),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/users", {
        params: { query: usersListQuery(undefined, inviterIdsParam) },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  return (
    <InvitationsSettingsView
      canManage={authority.canManage}
      createErrorMessage={
        createInvitation.isError ? createInvitation.error.message : undefined
      }
      grantableRoles={authority.grantableRoles}
      hasNextPage={invitations.hasNextPage}
      invitations={invitations.items}
      isCreatePending={createInvitation.isPending}
      isFetchingNextPage={invitations.isFetchingNextPage}
      isInvitationsPending={invitations.isPending}
      isInviteDrawerOpen={isInviteDrawerOpen}
      isTeamsPending={teams.isPending}
      listErrorMessage={invitations.errorMessage}
      listStateKey={invitations.listStateKey}
      revokeErrorInvitationId={
        revokeInvitation.isError ? revokeInvitation.variables : undefined
      }
      revokeErrorMessage={
        revokeInvitation.isError ? revokeInvitation.error.message : undefined
      }
      revokingInvitationId={
        revokeInvitation.isPending ? revokeInvitation.variables : undefined
      }
      teams={teams.data ?? []}
      teamsErrorMessage={teams.isError ? teams.error.message : undefined}
      usersById={userMap(users.data?.items ?? [])}
      usersErrorMessage={users.isError ? users.error.message : undefined}
      onCloseInvite={closeInviteDrawer}
      onLoadMore={invitations.fetchNextPage}
      onOpenInvite={openInviteDrawer}
      onResetRevoke={revokeInvitation.reset}
      onRevoke={revokeInvitation.mutateAsync}
      onSubmitCreate={createInvitation.mutate}
    />
  );
}

export interface InvitationsSettingsViewProps {
  canManage: boolean;
  createErrorMessage?: string;
  grantableRoles: MemberRole[];
  hasNextPage: boolean;
  invitations: Invitation[];
  isCreatePending: boolean;
  isFetchingNextPage: boolean;
  isInvitationsPending: boolean;
  isInviteDrawerOpen: boolean;
  isTeamsPending: boolean;
  listErrorMessage?: string;
  listStateKey: string;
  onCloseInvite: () => void;
  onLoadMore: () => void;
  onOpenInvite: () => void;
  onResetRevoke: () => void;
  onRevoke: (id: string) => Promise<void>;
  onSubmitCreate: (input: InvitationCreate) => void;
  revokeErrorInvitationId?: string;
  revokeErrorMessage?: string;
  revokingInvitationId?: string;
  teams: Team[];
  teamsErrorMessage?: string;
  usersById: ReadonlyMap<string, User>;
  usersErrorMessage?: string;
}

export function InvitationsSettingsView({
  canManage,
  createErrorMessage,
  grantableRoles,
  hasNextPage,
  invitations,
  isCreatePending,
  isFetchingNextPage,
  isInvitationsPending,
  isInviteDrawerOpen,
  isTeamsPending,
  listErrorMessage,
  listStateKey,
  onCloseInvite,
  onLoadMore,
  onOpenInvite,
  onResetRevoke,
  onRevoke,
  onSubmitCreate,
  revokeErrorInvitationId,
  revokeErrorMessage,
  revokingInvitationId,
  teams,
  teamsErrorMessage,
  usersById,
  usersErrorMessage,
}: InvitationsSettingsViewProps) {
  return (
    <Page variant="workspace">
      {/* §4b.3: the Invite action stays fixed while the table owns the
          remaining height, its own scroll, and a sticky column header. */}
      <PageHeader
        actions={
          canManage ? (
            <Button size="sm" type="button" onClick={onOpenInvite}>
              Invite
            </Button>
          ) : undefined
        }
      />
      <InvitationsTable
        canManage={canManage}
        hasNextPage={hasNextPage}
        invitations={invitations}
        isFetchingNextPage={isFetchingNextPage}
        isPending={isInvitationsPending}
        listErrorMessage={listErrorMessage}
        listStateKey={listStateKey}
        revokeErrorInvitationId={revokeErrorInvitationId}
        revokeErrorMessage={revokeErrorMessage}
        revokingInvitationId={revokingInvitationId}
        teams={teams}
        usersById={usersById}
        usersErrorMessage={usersErrorMessage}
        onLoadMore={onLoadMore}
        onOpenInvite={onOpenInvite}
        onResetRevoke={onResetRevoke}
        onRevoke={onRevoke}
      />
      {isInviteDrawerOpen ? (
        <InviteDrawer
          createErrorMessage={createErrorMessage}
          grantableRoles={grantableRoles}
          isCreatePending={isCreatePending}
          isTeamsPending={isTeamsPending}
          teams={teams}
          teamsErrorMessage={teamsErrorMessage}
          onDismiss={onCloseInvite}
          onSubmit={onSubmitCreate}
        />
      ) : null}
    </Page>
  );
}
